/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowUpRight, Linkedin, Play } from "lucide-react";
import Navbar from "../components/dmb/Navbar";
import Footer from "../components/dmb/Footer";
import Seo from "../components/dmb/Seo";
import { ASSETS, FOD_SIMULATOR_URL, BFM_ARTICLE_URL, YOUTUBE_WATCH, LINKEDIN, API_BASE } from "../lib/assets";

/* ====== Chiffres de l'accueil — valeurs par défaut, remplacées par l'API au chargement ====== */
const DEFAULT_FIGURES = [
    { value: "+65 M€", label: "de financements obtenus pour nos clients" },
    { value: "15 ans", label: "d'expérience bancaire cumulée" },
    { value: "3 groupes", label: "bancaires où nous avons exercé : BNP Paribas, Crédit Agricole, BPCE" },
    { value: "650 000 €", label: "d'économies annuelles obtenues avec FOD" },
];

const DEFAULT_OPERATIONS = [
    { type: "Refinancement · RCF", montant: "5 M€", secteur_ca: "Secteur confidentiel · ~50 M€ de CA", banques: "2 banques", mention: "Conseil de la société · 2026" },
    { type: "BFR", montant: "2 M€", secteur_ca: "Téléphonie · 30 M€ de CA", banques: "3 banques", mention: "Conseil de la société · 2026" },
    { type: "Trade finance", montant: "15 M€", secteur_ca: "Défense · 50 M€ de CA", banques: "4 banques", mention: "Conseil de la société · 2025-2026" },
];

/* ====== Hero (fond navy, titre blanc + fin verte) ====== */
function Hero() {
    return (
        <section id="hero" data-testid="hero" style={{ background: "#0F1B2D" }} className="text-white">
            <div className="container-dmb pt-14 lg:pt-24 pb-14 lg:pb-20">
                <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center">
                    <div>
                        <div className="surtitre surtitre-on-dark mb-5">
                            Financement &amp; stratégie financière · PME &amp; ETI
                        </div>
                        <h1 className="text-white" data-testid="hero-title">
                            De votre côté de la{" "}
                            <span style={{ color: "#4EAE32" }}>table.</span>
                        </h1>
                        <p className="mt-6 text-[18px] lg:text-[20px] text-dmb-on-dark max-w-[640px] leading-[1.65]">
                            Des anciens banquiers préparent, négocient et obtiennent
                            vos financements, puis vous aident à piloter votre
                            relation bancaire. Un seul interlocuteur, de la
                            stratégie au déblocage des fonds.
                        </p>
                        <div className="mt-9 flex flex-wrap gap-3">
                            <Link to="/contact" data-testid="hero-cta-primary" className="btn-dmb btn-primary">
                                Présenter un projet
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link to="/financement" data-testid="hero-cta-secondary" className="btn-dmb btn-outline-white">
                                Voir comment nous travaillons
                            </Link>
                        </div>
                    </div>

                    {/* Fiche financement — carte claire sur fond navy */}
                    <aside aria-label="Fiche de financement, exemple illustratif" data-testid="hero-fiche" className="bg-white text-dmb-navy rounded-[18px] p-6 lg:p-7">
                        <div className="flex items-center justify-between mb-4">
                            <div className="surtitre">Fiche de financement</div>
                            <span className="text-[12px] text-dmb-muted-2 italic">Exemple illustratif</span>
                        </div>
                        <dl className="divide-y divide-dmb-border-soft text-[14px]">
                            {[
                                ["Objet", "Croissance externe + BFR"],
                                ["Structure", "Prêt 7 ans + RCF"],
                                ["Dossier", "Prévisionnel · plan de trésorerie"],
                                ["Banques consultées", "6"],
                                ["Garanties", "Négociées"],
                                ["Statut", "Fonds débloqués"],
                            ].map(([k, v]) => (
                                <div key={k} className="flex justify-between py-2.5 gap-4">
                                    <dt className="text-dmb-muted-2">{k}</dt>
                                    <dd className="text-dmb-navy font-medium text-right">{v}</dd>
                                </div>
                            ))}
                        </dl>
                    </aside>
                </div>
            </div>
        </section>
    );
}

/* ====== Chiffres clés (fond blanc, sous le héros) — données admin ====== */
function Figures({ figures }) {
    return (
        <section data-testid="figures" className="bg-white border-b border-dmb-border">
            <div className="container-dmb py-14 lg:py-16">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-8 lg:gap-x-10" data-testid="hero-figures">
                    {figures.map((f, i) => (
                        <div key={i} data-testid={`figure-${i}`}>
                            <div className="text-dmb-navy font-bold text-[36px] lg:text-[44px] leading-none tracking-tight tabular-nums">
                                {f.value}
                            </div>
                            <div className="mt-3 text-[14px] text-dmb-muted leading-[1.55] max-w-[260px]">
                                {f.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ====== Métiers (3 cartes) ====== */
function Metiers() {
    return (
        <section id="metiers" data-testid="metiers" className="bg-white">
            <div className="container-dmb section-pad">
                <div className="max-w-[720px]">
                    <div className="surtitre mb-4">Nos métiers</div>
                    <h2>Trois métiers, un seul interlocuteur.</h2>
                </div>

                <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* 01 Financement */}
                    <article className="card-dmb p-7 flex flex-col" data-testid="metier-financement">
                        <div className="surtitre mb-2">01</div>
                        <h3 className="text-[22px] font-bold text-dmb-navy">Financement</h3>
                        <p className="mt-3 text-[15px] text-dmb-muted leading-[1.65]">
                            Nous obtenons la dette dont votre projet a besoin, aux bonnes conditions.
                        </p>
                        <ul className="mt-5 space-y-2 text-[14px] text-dmb-navy">
                            {["BFR, CAPEX, découvert, RCF", "Crédit-bail et financement d'actifs", "Financement de croissance externe", "Transition, trade finance, garanties"].map((x) => (
                                <li key={x} className="flex gap-2"><span className="text-dmb-green-dark">—</span>{x}</li>
                            ))}
                        </ul>
                        <Link to="/financement" className="mt-6 inline-flex items-center gap-1 text-dmb-green-dark font-semibold hover:text-dmb-navy">
                            Découvrir le financement <ArrowRight className="w-4 h-4" />
                        </Link>
                    </article>

                    {/* 02 Stratégie financière */}
                    <article id="strategie" className="card-dmb p-7 flex flex-col scroll-mt-24" data-testid="metier-strategie">
                        <div className="surtitre mb-2">02</div>
                        <h3 className="text-[22px] font-bold text-dmb-navy">Stratégie financière</h3>
                        <p className="mt-3 text-[15px] text-dmb-muted leading-[1.65]">
                            Nous travaillons à vos côtés comme un directeur financier, de 3 à 12 mois.
                        </p>
                        <ul className="mt-5 space-y-2 text-[14px] text-dmb-navy">
                            {["DAF externalisé", "Structuration du pool bancaire", "Prévisionnel et plan de trésorerie", "Formation de vos équipes"].map((x) => (
                                <li key={x} className="flex gap-2"><span className="text-dmb-green-dark">—</span>{x}</li>
                            ))}
                        </ul>
                        <Link to="/contact" className="mt-6 inline-flex items-center gap-1 text-dmb-green-dark font-semibold hover:text-dmb-navy">
                            En parler avec nous <ArrowRight className="w-4 h-4" />
                        </Link>
                    </article>

                    {/* 03 FOD — filet or + bouton or */}
                    <article className="rounded-[18px] p-7 flex flex-col text-white relative overflow-hidden" style={{ background: "linear-gradient(140deg, #0F1B2D 0%, #0A1422 100%)" }} data-testid="metier-fod">
                        <div className="absolute inset-x-0 top-0 h-1" style={{ background: "#D4A63A" }} />
                        <div className="surtitre surtitre-on-dark mb-2" style={{ color: "#D4A63A" }}>03 · FOD by DMB Partners</div>
                        <h3 className="text-[22px] font-bold text-white">Frais bancaires</h3>
                        <p className="mt-3 text-[15px] text-dmb-on-dark leading-[1.65]">
                            Nous auditons chaque ligne de vos relevés, puis nous renégocions avec vos banques. Mission menée en trois semaines en moyenne.
                        </p>
                        <ul className="mt-5 space-y-2 text-[14px] text-dmb-on-dark">
                            {["Utilisation de vos comptes", "Monétique", "International (change, devises)"].map((x) => (
                                <li key={x} className="flex gap-2"><span style={{ color: "#D4A63A" }}>—</span>{x}</li>
                            ))}
                        </ul>
                        <a href={FOD_SIMULATOR_URL} target="_blank" rel="noopener noreferrer" data-testid="metier-fod-link" className="mt-6 inline-flex items-center justify-center gap-2 self-start h-12 px-5 rounded-lg font-semibold text-dmb-navy transition-transform hover:-translate-y-0.5" style={{ background: "#D4A63A" }}>
                            Simuler mes économies <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </article>
                </div>
            </div>
        </section>
    );
}

/* ====== Comité de crédit (tableau 2 colonnes, 5 lignes) ====== */
function Comite() {
    const rows = [
        ["La capacité de remboursement", "Un prévisionnel et un plan de trésorerie construits et argumentés"],
        ["La qualité de la relation", "Le choix des banques à consulter, selon le projet et votre historique"],
        ["Les garanties", "La négociation des sûretés et des cautions personnelles"],
        ["Les covenants", "Des ratios calibrés pour votre activité, pas pour le pire scénario"],
        ["Le prix", "Une mise en concurrence réelle entre établissements"],
    ];
    return (
        <section data-testid="comite" className="bg-dmb-page border-t border-dmb-border">
            <div className="container-dmb section-pad">
                <div className="max-w-[760px] mb-10">
                    <div className="surtitre mb-4">Notre valeur ajoutée</div>
                    <h2>Nous préparons ce que le comité de crédit va regarder.</h2>
                    <p className="mt-5 text-dmb-muted">
                        Un dossier se joue avant d'arriver en comité. Nous avons été
                        de l'autre côté : nous savons ce qui fait dire oui, et à
                        quelles conditions.
                    </p>
                </div>
                <div className="overflow-hidden rounded-[18px] bg-white border border-dmb-border">
                    <table className="w-full text-[15px]">
                        <thead>
                            <tr className="bg-white border-b-2 border-dmb-navy">
                                <th className="text-left py-4 px-6 font-semibold text-dmb-navy w-1/2">Ce que regarde la banque</th>
                                <th className="text-left py-4 px-6 font-semibold text-dmb-navy w-1/2">Ce que nous préparons</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(([a, b], i) => (
                                <tr key={i} className="border-t border-dmb-border-soft">
                                    <td className="py-4 px-6 text-dmb-navy font-medium">{a}</td>
                                    <td className="py-4 px-6 text-dmb-muted">{b}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}

/* ====== 4 étapes (filet coloré en haut) ====== */
function Etapes() {
    const steps = [
        { n: "01", t: "Diagnostic", d: "Rendez-vous de stratégie financière et bancaire : votre besoin, votre structure, vos banques.", color: "#4EAE32" },
        { n: "02", t: "Dossier", d: "Un dossier de financement complet, rédigé comme une banque l'attend.", color: "#4EAE32" },
        { n: "03", t: "Mise en concurrence", d: "Nous présentons votre demande aux banques, leaseurs et fonds de dette pertinents.", color: "#4EAE32" },
        { n: "04", t: "Signature", d: "Négociation des conditions, des garanties et des covenants, jusqu'au déblocage.", color: "#4EAE32" },
    ];
    return (
        <section data-testid="etapes" className="bg-white border-t border-dmb-border">
            <div className="container-dmb section-pad">
                <div className="max-w-[760px]">
                    <div className="surtitre mb-4">Notre méthode</div>
                    <h2>De la stratégie au déblocage des fonds.</h2>
                </div>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {steps.map((s) => (
                        <article key={s.n} data-testid={`etape-${s.n}`} className="card-dmb p-6 relative overflow-hidden">
                            <div className="absolute inset-x-0 top-0 h-1" style={{ background: s.color }} />
                            <div className="surtitre mb-3 mt-1">{s.n}</div>
                            <h3 className="text-[18px] font-bold text-dmb-navy">{s.t}</h3>
                            <p className="mt-3 text-[14px] text-dmb-muted leading-[1.65]">{s.d}</p>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ====== Opérations récentes (fond navy) — données admin ====== */
function Operations({ ops }) {
    return (
        <section id="operations" data-testid="operations" className="scroll-mt-24" style={{ background: "#0F1B2D" }}>
            <div className="container-dmb section-pad text-white">
                <div className="max-w-[720px] text-center mx-auto">
                    <div className="surtitre surtitre-on-dark mb-4">Opérations récentes</div>
                    <h2 className="text-white">Ce que nous avons financé.</h2>
                    <p className="mt-5 text-dmb-on-dark">Opérations anonymisées, avec l'accord de nos clients.</p>
                </div>
                <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {ops.map((r, i) => (
                        <article key={r.type} data-testid={`op-${i}`} className="rounded-[18px] p-7 text-center flex flex-col items-center relative overflow-hidden" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)" }}>
                            <div className="surtitre surtitre-on-dark mb-3 uppercase">{r.type}</div>
                            <div className="font-bold tabular-nums text-white" style={{ fontSize: "46px", lineHeight: 1 }}>{r.montant}</div>
                            <div className="mt-5 w-10 h-0.5" style={{ background: "#4EAE32" }} />
                            <div className="mt-5 text-[14px] text-dmb-on-dark leading-[1.65]">{r.secteur_ca}</div>
                            <div className="mt-1 text-[14px] text-dmb-on-dark leading-[1.65]">{r.banques}</div>
                            <div className="mt-5 pt-4 border-t border-white/10 w-full text-[13px] text-dmb-on-dark-2">{r.mention}</div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ====== Cabinet — carte horizontale avec portrait rond 84px ====== */
function Cabinet() {
    const people = [
        { name: "Edina Berisha", role: "Cofondatrice", photo: ASSETS.edina, linkedin: LINKEDIN.edina, bio: "Ancienne chargée d'affaires entreprises senior chez BNP Paribas, passée par la Banque Palatine et la Caisse d'Épargne Rhône Alpes (Groupe BPCE). Diplômée en finance de l'IAE Lyon." },
        { name: "Hugo Daugeron-Mijoin", role: "Cofondateur", photo: ASSETS.hugo, linkedin: LINKEDIN.hugo, bio: "Ancien chargé d'affaires entreprises senior à la Banque Palatine (Groupe BPCE), passé par LCL, la Caisse d'Épargne et BNP Paribas. Diplômé en banque-finance de Paris 1 Panthéon-Sorbonne." },
    ];
    return (
        <section id="cabinet" data-testid="cabinet" className="bg-dmb-page border-t border-dmb-border scroll-mt-24">
            <div className="container-dmb section-pad">
                <div className="max-w-[760px]">
                    <div className="surtitre mb-4">15 ans d'expérience bancaire cumulés au service des PME / ETI</div>
                    <h2>Ils connaissent les rouages bancaires, de l'intérieur.</h2>
                    <p className="mt-6 text-[17px] text-dmb-muted leading-[1.7]">
                        Edina BERISHA et Hugo DAUGERON-MIJOIN ont créé en 2024 FOD by
                        DMB Partners, le seul service permettant aux dirigeants de PME
                        / ETI de comprendre leurs frais bancaires, de réduire ces
                        coûts financiers et surtout de remettre du lien avec leurs
                        banquiers.
                    </p>
                </div>
                <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {people.map((p) => (
                        <article key={p.name} data-testid={`person-${p.name.toLowerCase().replace(/\s+/g, "-")}`} className="card-dmb p-7 flex gap-5 items-start">
                            <img
                                src={p.photo}
                                alt={p.name}
                                loading="lazy"
                                width={84}
                                height={84}
                                className="rounded-full object-cover shrink-0"
                                style={{ width: 84, height: 84 }}
                            />
                            <div className="flex-1">
                                <h3 className="text-[18px] font-bold text-dmb-navy">{p.name}</h3>
                                <div className="mt-0.5 text-[13px] text-dmb-muted">
                                    {p.role} ·{" "}
                                    <a href={p.linkedin} target="_blank" rel="noopener noreferrer" className="link-green inline-flex items-center gap-1">
                                        LinkedIn <Linkedin className="w-3.5 h-3.5" />
                                    </a>
                                </div>
                                <p className="mt-3 text-[14px] text-dmb-muted leading-[1.65]">{p.bio}</p>
                            </div>
                        </article>
                    ))}
                </div>

                {/* Mention d'adhésion MEDEF Paris */}
                <div
                    data-testid="cabinet-medef"
                    className="mt-10 pt-6 flex flex-col items-center text-center md:flex-row md:text-left gap-[22px]"
                    style={{ borderTop: "1px solid var(--dmb-border)" }}
                >
                    <a
                        href="https://www.medefparis.fr/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="MEDEF Paris"
                        data-testid="cabinet-medef-link"
                        className="shrink-0"
                    >
                        <img
                            src="/logos/medef-paris.png"
                            alt="MEDEF Paris"
                            className="h-11 md:h-14 w-auto"
                            loading="lazy"
                        />
                    </a>
                    <div
                        className="hidden md:block w-px h-11"
                        style={{ background: "var(--dmb-border)" }}
                    />
                    <div>
                        <div className="text-dmb-navy text-[16px] font-semibold leading-snug">
                            DMB Partners est adhérent du MEDEF Paris
                        </div>
                        <div className="text-dmb-muted text-[14px] leading-snug mt-1">
                            Edina et Hugo y représentent le cabinet.
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ====== Médias ====== */
function Medias() {
    return (
        <section id="medias" data-testid="medias" className="bg-white border-t border-dmb-border scroll-mt-24">
            <div className="container-dmb section-pad">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-14 items-center">
                    <div>
                        <div className="surtitre mb-4">Médias</div>
                        <h2>Invités de BFM Business, Focus Entreprises.</h2>
                        <p className="mt-6 text-[17px] text-dmb-muted leading-[1.7]">
                            Edina et Hugo présentent leur approche du financement et de la relation bancaire des PME et ETI.
                        </p>
                        <a href={BFM_ARTICLE_URL} target="_blank" rel="noopener noreferrer" data-testid="medias-cta" className="mt-7 btn-dmb btn-outline-dark">
                            Voir l'émission <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </div>
                    <a href={YOUTUBE_WATCH} target="_blank" rel="noopener noreferrer" aria-label="Lire la vidéo YouTube" data-testid="medias-thumb" className="relative block aspect-[16/10] rounded-2xl overflow-hidden group">
                        <img src={ASSETS.bfmStudio} alt="DMB Partners sur BFM Business" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />
                        <div className="absolute left-5 bottom-5 w-16 h-16 rounded-full flex items-center justify-center shadow-xl" style={{ background: "#4EAE32" }}>
                            <Play className="w-6 h-6 text-white ml-1" fill="white" />
                        </div>
                    </a>
                </div>
            </div>
        </section>
    );
}

/* ====== CTA final — Accueil ====== */
function CTAFinal() {
    return (
        <section id="contact" data-testid="cta-final" className="scroll-mt-24 bg-white border-t border-dmb-border">
            <div className="container-dmb section-pad">
                <div className="rounded-[20px] px-8 py-14 lg:px-14 lg:py-16 text-white flex flex-col lg:flex-row gap-8 lg:gap-10 items-start lg:items-center justify-between" style={{ background: "#0F1B2D" }}>
                    <div className="max-w-[640px]">
                        <div className="surtitre surtitre-on-dark mb-3">Contact</div>
                        <h2 className="text-white">Un projet de financement ? Parlons-en.</h2>
                        <p className="mt-4 text-[16px] text-dmb-on-dark leading-[1.7]">
                            Premier échange de 30 minutes, sans engagement. Réponse sous 24 h ouvrées.
                        </p>
                    </div>
                    <Link to="/contact" data-testid="cta-final-primary" className="btn-dmb btn-primary">
                        Présenter un projet <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </section>
    );
}

export default function HomePage() {
    const [figures, setFigures] = useState(DEFAULT_FIGURES);
    const [operations, setOperations] = useState(DEFAULT_OPERATIONS);

    useEffect(() => {
        axios
            .get(`${API_BASE}/settings/home-figures`)
            .then((res) => {
                if (res.data?.figures?.length) setFigures(res.data.figures);
                if (res.data?.operations?.length) setOperations(res.data.operations);
            })
            .catch(() => {
                /* valeurs par défaut conservées si l'API est indisponible */
            });
    }, []);

    return (
        <div data-testid="home-page" className="bg-white">
            <Seo
                title="DMB Partners | Financement et stratégie financière PME-ETI"
                description="Des anciens banquiers obtiennent, structurent et négocient les financements des PME et ETI. Cabinet indépendant à Paris, un seul interlocuteur."
                path="/"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "FinancialService",
                    "@id": "https://www.dmbpartners.fr/#financialservice",
                    name: "DMB Partners",
                    description: "Cabinet indépendant de financement et de stratégie financière pour PME et ETI.",
                    url: "https://www.dmbpartners.fr",
                    email: "contact@fodbydmb.com",
                    address: { "@type": "PostalAddress", streetAddress: "149 avenue du Maine", postalCode: "75014", addressLocality: "Paris", addressCountry: "FR" },
                    areaServed: { "@type": "Country", name: "France" },
                    parentOrganization: { "@id": "https://www.dmbpartners.fr/#organization" },
                    sameAs: ["https://www.linkedin.com/company/dmb-partners/"],
                }}
            />
            <Navbar />
            <main>
                <Hero />
                <Figures figures={figures} />
                <Metiers />
                <Comite />
                <Etapes />
                <Operations ops={operations} />
                <Cabinet />
                <Medias />
                <CTAFinal />
            </main>
            <Footer />
        </div>
    );
}
